<template>
  <div>
  <el-dialog
    class="vendor-customer-dialog"
    size="small"
    @open="focusOnFirstField"
    :top="type === 'customer' ? '10%' : '15%'"
    :show-close="false"
    :close-on-click-modal="false"
    :close-on-press-escape="false"
    :visible="visible">
    <el-row type="flex" align="middle" style="padding-bottom: 12px; margin-bottom: 6px;">
      <el-col style="font-size: 24px;">
        <el-row type="flex" align="middle">
          {{ editing ? `Edit ${capitalizedType}` : `New ${capitalizedType}` }}
        </el-row>
      </el-col>
      <el-col>
        <el-row type="flex" align="middle" justify="end" style="color: darkred;">
          {{ warning || '' }}
        </el-row>
      </el-col>
      <template v-if="editing">
        <el-button stye="font-size: 13px;" @click="() => openUploadDocument('uploadDocument')">
          Files {{ numberOfFiles && numberOfFiles > 0 ? `(${numberOfFiles})` : '' }}
        </el-button>
      </template>
    </el-row>
    <el-row type="flex" v-loading.body="loading" element-loading-text="Loading form..." style="min-height: 520px;">
      <el-col :style="`visibility: ${loading ? 'hidden' : 'visible'}`">
        <el-row type="flex" align="middle">
          <StandardForm
            :entityType="type"
            :title="`General ${capitalizedType} Information`"
            :fields="customerVendorFields"
            :visible="visible"
            :form.sync="form"
            :idPrefix="'w-'"
            :formNum="1"
            :active="activeForm"
            @focusEvent="handleFocus"
            @blurEvent="handleBlur"
            @formChanged="formChanged">
          </StandardForm>
        </el-row>
        <el-row type="flex" align="middle">
          <StandardForm
            :entityType="type"
            :visible="visible"
            title="Contact Address"
            :fields="contactAddressFields"
            :form.sync="form"
            :idPrefix="'x-'"
            :formNum="2"
            :active="activeForm"
            @focusEvent="handleFocus"
            @blurEvent="handleBlur"
            @formChanged="formChanged"
            @optionSelected="handleBlur">
          </StandardForm>
        </el-row>
        <el-row type="flex" align="middle">
          <StandardForm
            :entityType="type"
            :visible="visible"
            title="Mailing Address"
            :fields="mailingAddressFields"
            :form.sync="form"
            :idPrefix="'y-'"
            :formNum="3"
            :active="activeForm"
            @focusEvent="handleFocus"
            @blurEvent="handleBlur"
            @formChanged="formChanged">
          </StandardForm>
        </el-row>
        <el-row type="flex" align="middle">
          <StandardForm
            :entityType="type"
            :visible="visible"
            title="Billing Address"
            :fields="billingAddressFields"
            :form.sync="form"
            :idPrefix="'z-'"
            :formNum="4"
            :active="activeForm"
            @focusEvent="handleFocus"
            @blurEvent="handleBlur"
            @formChanged="formChanged">
          </StandardForm>
        </el-row>
      </el-col>
    </el-row>
    <span slot="footer" class="dialog-footer">
      <el-button
        v-if="modelId && type === 'vendor'"
        tabindex="37"
        :disabled="loading || numberOfFiles > 0 || vendorHasTransactions"
        :loading="deletingVendor"
        type="danger"
        class="delete-vendor"
        @click="confirmDeletingVendor">
        Delete
      </el-button>
      <el-button
        tabindex="35"
        :disabled="savingVendor || savingCustomer || !!updatingVendor || !!updatingCustomer || loading"
        @click="changeVisibility(false)">
        {{ saved ? 'Close' : 'Cancel' }}
      </el-button>
      <el-button
        tabindex="36"
        :disabled="loading"
        :loading="savingVendor || savingCustomer || !!updatingVendor || !!updatingCustomer"
        type="primary"
        @click="submitForm">
        Save
      </el-button>
      <el-button
        v-if="editing && userCanCreateVendor"
        tabindex="36"
        :disabled="loading || savingVendor || savingCustomer || !!updatingVendor || !!updatingCustomer"
        type="primary"
        icon="plus"
        @click="handleMainBtnClick">
      </el-button>
    </span>
  </el-dialog>
  <el-dialog
      id="docs-dialog"
      title="View/Upload Documents"
      size="small"
      top="10%"
      :visible.sync="dropzoneVisible"
      :close-on-press-escape="true"
      :close-on-click-modal="false">
      <document-dialog
        :transaction="form"
        :dz-options="setupDropzoneOptions()"
        @upload-success="dzSuccess"
        @upload-error="dzFailure"
        @upload-remove="dzRemove"
        :key="reRender"
      />
  </el-dialog>
  <el-dialog
      title="Delete Vendor"
      :visible.sync="deleteVendorModalVisible"
      size="tiny"
      :show-close="false"
      :close-on-press-escape="false"
      :close-on-click-modal="false">
      <span>
        Are you sure you want to delete this vendor
      </span>
      <span slot="footer" class="dialog-footer">
        <el-button class="exit-modal-btn" type="primary" @click="cancelVendorDelete" :disabled="deletingVendor">
          No
        </el-button>
        <el-button type="primary" @click="deleteVendor" :loading="deletingVendor">
          Yes
        </el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
  import StandardForm from '@/components/StandardForm.vue'
  import { mapGetters } from 'vuex'
  import { notify } from '@/utils/helpers/Notify'
  import { validate } from '@/utils/helpers/Validate'
  import { vendorMixins } from '@/Mixins/VendorMixins'
  import { systemViewMixins } from '@/Mixins/SystemViewMixins'
  import cityMixin from '@/Mixins/CityMixin'
  import { getHeaders } from '@/utils/api'
  import Axios from 'axios'
  import { capitalizeFirstLetter } from '@/utils/helpers/StringOperations'
  import { payload as accountActionPayload } from '@/components/RouteComponents/Accounts/actionPayload'
  import { payload as salesrepActionPayload } from '@/components/RouteComponents/Salesreps/actionPayload'
  import AutocompleteLocalSearch from '@/utils/helpers/Search/AutocompleteLocalSearch'
  import Crypto from 'crypto'
  import DocumentDialog from '@/components/DocumentDialog.vue'

  export default {
    components: { StandardForm, DocumentDialog },
    props: {
      type: {
        'required': true,
        'default': 'vendor'
      },
      modelId: {
        'default': () => {
          return null
        },
        'type': String
      }
    },
    mixins: [ vendorMixins, systemViewMixins, cityMixin ],
    data () {
      let form = {
        code: '',
        parent: { label: '', value: '' },
        name: '',
        description: '',
        active: true,
        card_id: '',
        petty_cash_enabled: false,
        petty_cash_p_card_enabled: false,
        payee_name: '',
        alias_name: '',
        alias_type: { label: 'Name', value: 'NAME' },
        default_payment_type: { label: 'Check', value: 'CHECK' },
        default_accounts: { label: '', value: '' },
        petty_cash_account: { label: '', value: '' },
        petty_cash_p_card_account: {label: '', value: ''},
        default_address: { label: 'Contact Address', value: 'CONTACT' },
        address_mailing: '',
        address_mailing_line1: '',
        address_mailing_line2: '',
        address_mailing_city: '',
        address_mailing_state: { label: '', value: '' },
        address_mailing_country: { label: '', value: '' },
        address_mailing_postal: '',
        address_billing: '',
        address_billing_line1: '',
        address_billing_line2: '',
        address_billing_city: '',
        address_billing_state: { label: '', value: '' },
        address_billing_country: { label: '', value: '' },
        address_billing_postal: '',
        address_contact: '',
        address_contact_line1: '',
        address_contact_line2: '',
        address_contact_city: '',
        address_contact_state: { label: '', value: '' },
        address_contact_country: { label: '', value: '' },
        address_contact_postal: '',
        account_number: '',
        due_days: '',
        entity_type: { label: 'Unknown', value: 'UNKNOWN' },
        tax_id: '',
        default_tax_code: { label: '', value: '' },
        tax_name: '',
        work_state: { label: '', value: '' },
        phone_home: '',
        email: '',
        phone_cell: '',
        // Customer-specific fields
        credit_limit_decimal: '',
        currency: { label: '', value: '' },
        payment_term: '',
        receivable_account: { label: '', value: '' },
        sales_rep: { label: '', value: '' },
        petty_cash_custodian: false,
        petty_cash_custodian_account: { label: '', value: '' },
        billing_notification_method: { label: 'E-mail', value: 'EML' },
        default_payment_method: { label: 'Check', value: 'CHK' },
        comments: '',
        ach_account_number: '',
        ach_routing_number: '',
        deposit_account: { label: '', value: '' },
        fatca_filing_requirement: false
      }
      return {
        inputChange: false,
        validationData: {},
        validationModel: null,
        form,
        formInitState: null,
        loading: false,
        saved: false,
        editing: false,
        warning: '',
        activeForm: 1,
        instanceUrl: '',
        headers: undefined,
        cityType: undefined,
        isCityModel: undefined,
        clearingAccounts: [],
        clearingAccountActionPayload: {},
        numberOfFiles: 0,
        vendorHasTransactions: true,
        deleteVendorModalVisible: false,
        dropzoneVisible: false,
        reRender: 0,
        deletingVendor: false
      }
    },
    methods: {
      handleFocus (formNum) {
        this.activeForm = formNum
      },
      /**
       * Governs the actions taken when the primary button is clicked in the vendor create/edit modal
       * If this.saved is true, clears the form and turns edit mode off, else submits the form.
       */
      handleMainBtnClick () {
        if (this.userCanCreateVendor) {
          this.clearForm()
          this.$emit('updateModelIdProp', null)
          this.editing = false
        } else {
          this.submitForm()
        }
        this.focusOnFirstField()
      },

      async checkDuplicateVendor (fieldName) {
        let headers = getHeaders(this.$store.getters.getToken)
  
        try {
          const response = await Axios.post(this.instanceUrl + 'vendors/validate', {[fieldName]: this.form[fieldName]}, { headers })
          if (response.status === 200) {
            if (response.data.status === 'failed') {
              this.validationData = response.data
              const h = this.$createElement
              this.validationModel = this.$message({
                message: h('p', null, [
                  h('span', null, this.validationData.message),
                  h('button', { on: { click: () => this.openExisting() } }, `Discard current entry & view existing vendor`),
                  h('button', { on: { click: () => this.validationModel.close() } }, `Proceed`)
                ]),
                type: 'warning',
                duration: 0,
                showClose: true
              })
              return
            }
          } else {
            throw (response)
          }
        } catch (e) {
          throw (e)
        }
      },
  
      openExisting () {
        this.validationModel.close()
        this.$emit('updateModelIdProp', this.validationData.vendor_id)
        this.inputChange = false
      },
  
      /**
       * Governs visibility of modal
       */
      changeVisibility (visible) {
        if (!visible) {
          this.clearForm()
          this.$emit('updateModelIdProp', null)
          this.editing = false
          this.saved = false
        }
        if (!this.inDataEntryView) {
          this.$store.dispatch('isEditingForm', false)
        }
        this.$store.dispatch('changeModalVisibility', { entityType: 'customerVendorForm', visible })
      },
      /**
       * Sets addresses as nested objects within the 'form' object because the API
       * expects new addresses to be nested objects without IDs and edits to existing
       * addresses to be nested objects with IDs
       */
      async submitAddresses () {
        let all = {
          address_mailing: this.mailingAddressFields,
          address_contact: this.contactAddressFields,
          address_billing: this.billingAddressFields
        }
        for (let a in all) {                                  // Go through each address
          const hasAtLeastOneField = all[a].some(v => {       // Make sure at least one field has been filled
            if (!this.form[v.name]) return false
            if (this.form[v.name].hasOwnProperty('value')) return !!this.form[v.name].value
            return !!this.form[v.name]
          })
          if (!hasAtLeastOneField) continue                   // If no fields have been filled, skip this address
          let subsetForm = {}
          all[a].forEach(v => {
            const val = this.form[v.name]
            subsetForm[v.name.split('_')[2]] = val.hasOwnProperty('value') ? val.value : val
          })

          // New code here to check if the city property in the form should be a string or an ID to fit in the new city model.
          // I am assuming city is not blank at this point, but something to look at if the below does not work.

          // Check if database supports models.  I checked on submitForm, but safe to check again here if needed.
          if (this.isCityModel === undefined) {
            await this.checkCityModel()
          }

          // Not using the new city model.  No need to check any further.
          if (this.isCityModel === false) {
            this.cityType = 'string'

          // City model is on this instance.  City needs to be converted to to an id.
          } else if (this.isCityModel === true) {
            // City is a string. get the id or create a new one.
            if (typeof subsetForm.city === 'string' && isNaN(subsetForm.city)) {
              this.cityType = 'string'

              // Check if the city name is in the system.  If not, create it.
              let cityId = await this.getCityByNameAndState(subsetForm.city, subsetForm.state)
              if (cityId) {
                subsetForm.city = cityId
              } else {
                // Create City
                let payload = {
                  name: subsetForm.city,
                  code: `${Crypto.randomBytes(5).toString('hex')}`,
                  state: subsetForm.state
                }
                let cityId = await this.addNewCity(payload)
                subsetForm.city = cityId
                let _this = this
                setTimeout(function () {
                  _this.$store.dispatch('fetchCitiesWithQuery')
                }, 500)
              }

            // City is already an id.
            } else {
              this.cityType = 'number'
              subsetForm.city = parseInt(subsetForm.city)
            }

            // If here, city is good
            this.form[a] = subsetForm
            this.setFormId(a)
          } else {
            notify(this, 'warning', 'Please specify valid address attributes')
            return false
          }
        }
        return true
      },
      /**
       * Formats the default_accounts field
       */
      formatDefaultAccounts () {
        let ret = []
        if (this.form.default_accounts && this.form.default_accounts.value) {
          ret.push(this.form.default_accounts.value)
        }
        return ret
      },
      /**
       * Submits the form
       */
      async submitForm () {
        this.saved = false
        await this.checkCityModel()
        try {
          await this.submitAddresses()
        } catch (error) {
          console.error(error)
          return
        }
        let form = Object.assign({}, this.form)
        for (let f in this.form) {
          if ((form[f] === '' && !['email', 'ach_account_numbe', 'ach_routing_number'].some(v => f.includes(v))) || form[f] === null || ['_mailing_', '_billing_', '_contact_'].some(v => f.includes(v))) delete form[f]
        }
        let validationErrors = validate(this.form, [
          ...this.customerVendorFields,
          ...this.contactAddressFields,
          ...this.mailingAddressFields,
          ...this.billingAddressFields
        ])
        if (!validationErrors.messages) {
          for (let f in form) {
            if (form[f].hasOwnProperty('value')) form[f] = this.form[f].value
            switch (f) {
              case 'default_markup_amount_decimal':
              case 'default_markup_rate':
              case 'credit_limit_decimal':
                form[f] = parseFloat(this.form[f])
            }
            if ((form[f] === '' && !['email', 'ach_account_numbe', 'ach_routing_number'].some(v => f.includes(v))) || form[f] === null) delete form[f]
          }
          form.default_accounts = this.formatDefaultAccounts()
          const action = this.editing || !this.userCanCreateVendor
            ? this.$store.dispatch(`update${this.capitalizedType}`, { id: this.modelId || form.id, data: form })
            : this.$store.dispatch(`save${this.capitalizedType}`, form)
          let id, code
          try {
            const r = await action
            if (!r || (r.status && r.status !== 200)) {
              throw new Error(r.statusText || 'Unable to save')
            }
            id = r && r.response ? r.response.id : ''
            code = r && r.response ? r.response.code : ''
            const promises = await this.$store.dispatch('findDependencies', r.entities)
            await Promise.all(promises)
            this.saved = true
            if (this.inDataEntryView) {
              this.$emit('populateFieldWithNewModel', { type: this.type, getter: `get${this.capitalizedType}ById`, id })
              this.changeVisibility(false)
              this.$notify({ title: `${this.capitalizedType} Saved`, message: code ? `Code: ${code}` : '', type: 'success' })
            } else {
              this.form.code = code
              this.form['id'] = id
              this.$emit('updateModelIdProp', id)
              this.editing = true
              this.$notify({ title: 'Success', message: `${this.capitalizedType} Saved`, type: 'success' })
              if (!this.inDataEntryView) this.$store.dispatch('isEditingForm', false)
            }
          } catch (e) {
            console.log(e)
            notify(this, 'error', `Error saving ${this.type}`)
          }
        } else {
          let offset = 0                        // Set offset so that notifs don't overlap each other
          validationErrors.messages.map(v => {  // Render validation error notifs
            notify(this, 'warning', v, offset)
            offset += 100
          })
        }
      },
      /**
       * Edit an existing vendor
       * @param modelId   The ID of the vendor to be edited
       */
      async editExistingModel (modelId) {
        this.clearForm()
        const model = this[`get${this.capitalizedType}ById`](modelId)
        let modelAccounts = []
        let parentVendor = { label: '', value: '' }
        if (this.type === 'customer') {
          modelAccounts = [model.deposit_account, model.petty_cash_custodian_account]
        }
        if (model.petty_cash_account) {
          modelAccounts.push(model.petty_cash_account)
        }
        if (model.petty_cash_p_card_account) {
          modelAccounts.push(model.petty_cash_p_card_account)
        }
        if (model.default_accounts.length) {
          modelAccounts.push(model.default_accounts[0])
        }
        if (model.parent) {
          await this.$store.dispatch('fetchVendorById', model.parent)
          const vendor = this.$store.getters.getVendorById(model.parent)
          parentVendor.label = vendor.name
          parentVendor.value = vendor.id
        }
        if (modelAccounts.length) {
          await this.$store.dispatch('fetchAccountsWithQuery', { id__in: [...new Set(modelAccounts)], page_size: modelAccounts.length })
        }
        for (let p in model) {
          if (model[p] === null || model[p] === '' || !Object.keys(this.form).includes(p)) continue
          switch (p) {
            case 'parent':
              this.form['parent'] = Object.assign({}, parentVendor)
              break
            case 'default_accounts':
              this.form[p] = Object.assign({}, {
                label: model[p] && model[p].length ? this.getAccountById(model[p][0]).code + ' - ' + this.getAccountById(model[p][0]).name : '',
                value: model[p] && model[p].length ? this.getAccountById(model[p][0]).id : ''
              })
              break
            case 'petty_cash_account':
            case 'petty_cash_custodian_account':
            case 'receivable_account':
            case 'deposit_account':
            case 'petty_cash_p_card_account':
              this.form[p] = Object.assign({}, {
                label: model[p] ? this.getAccountById(model[p]).code + ' - ' + this.getAccountById(model[p]).name : '',
                value: model[p] ? this.getAccountById(model[p]).id : ''
              })
              break
            case 'address_mailing':
            case 'address_billing':
            case 'address_contact':
              const addressModel = this.getAddressById(model[p])
              if (!(addressModel && Object.keys(addressModel).length)) break
              for (let a in addressModel) {
                let tmp = {}
                if (a === 'state') {
                  tmp = Object.assign({})
                  if (addressModel[a] && this.getStateById(addressModel[a])) {
                    tmp['label'] = this.getStateById(addressModel[a]).name
                    tmp['value'] = this.getStateById(addressModel[a]).id
                  } else {
                    tmp['label'] = ''
                    tmp['value'] = ''
                  }
                  this.form[p + `_${a}`] = Object.assign({}, tmp)
                } else if (a === 'country') {
                  tmp = Object.assign({})
                  if (addressModel[a] && this.getCountryById(addressModel[a])) {
                    tmp['label'] = this.getCountryById(addressModel[a]).name
                    tmp['value'] = this.getCountryById(addressModel[a]).id
                  } else {
                    tmp['label'] = ''
                    tmp['value'] = ''
                  }
                  this.form[p + `_${a}`] = Object.assign({}, tmp)
                } else if (a === 'city' && addressModel[a] && this.isCityModel && typeof addressModel[a] === 'number') {
                  this.cityType = typeof addressModel[a]
                  let p1 = this.getCityById(addressModel[a])
                  p1.then((cityName) => {
                    this.form[p + `_${a}`] = cityName
                  })
                } else {
                  this.form[p + `_${a}`] = addressModel[a]
                }
              }
              break
            case 'due_days':
              this.form[p] = model[p].toString()
              break
            case 'work_state':
              this.form[p] = Object.assign({}, {
                label: model[p] ? this.getStateById(model[p]).name : '',
                value: model[p] ? this.getStateById(model[p]).id : ''
              })
              break
            case 'currency':
              this.form[p] = Object.assign({}, {
                label: model[p] ? this.getCurrencyById(model[p]).code : '',
                value: model[p] ? this.getCurrencyById(model[p]).id : ''
              })
              break
            case 'sales_rep':
              this.form[p] = Object.assign({}, {
                label: model[p] ? this.getSalesrepById(model[p]).name : '',
                value: model[p] ? this.getSalesrepById(model[p]).id : ''
              })
              break
            case 'payment_term':
              this.form[p] = `${model[p]}`
              break
            case 'alias_type':
            case 'entity_type':
            case 'default_payment_type':
            case 'default_address':
            case 'default_payment_method':
            case 'billing_notification_method':
              this.form[p] = Object.assign({}, {
                label: '',
                value: model[p]
              })
              break
            case 'default_tax_code':
              if (model[p]) {
                this.form[p] = Object.assign({}, {
                  label: model[p] ? this.getTaxcodeById(model[p]).name : '',
                  value: model[p] ? model[p] : ''
                })
              }
              break
            default:
              this.form[p] = model[p]
          }
        }
        this.initialForm = JSON.stringify(this.form)
        let response = this.setupDropzoneOptions()
        if (response) this.getFileCount(response)
        if (this.type === 'vendor') {
          this.checkVendorHasTransactions()
        }
        this.loading = false
      },
      /**
       * check vendor has transactions or files uploaded
       */
      confirmDeletingVendor () {
        this.deletingVendor = true
        if (this.vendorHasTransactions || this.numberOfFiles > 0) {
          this.deletingVendor = false
          this.$notify({ title: 'Error', message: `Cannot delete this vendor. Vendor has transactions or files`, type: 'error' })
          return
        }

        if (this.checkVendorHasTransactions()) {
          this.deletingVendor = false
          this.$notify({ title: 'Error', message: `Cannot delete this vendor. Vendor has transactions or files`, type: 'error' })
        } else {
          // Confirmation to delete
          this.deletingVendor = false
          this.deleteVendorModalVisible = true
        }
      },
      cancelVendorDelete () {
        this.deleteVendorModalVisible = false
      },
      /**
       * Delete an existing vendor
       */
      deleteVendor () {
        this.deletingVendor = true
        this.$store.dispatch('deleteVendor', { id: this.modelId })
          .then(r => {
            if (r && r.id && r.id === this.modelId) {
              this.$notify({ title: 'Success', message: `Vendor deleted successfully`, type: 'success' })
              this.deletingVendor = false
              this.deleteVendorModalVisible = false
              this.changeVisibility(false)
            } else {
              this.deletingVendor = false
              this.deleteVendorModalVisible = false
            }
          })
          .catch(error => {
            console.log(error)
            this.deletingVendor = false
            this.deleteVendorModalVisible = false
          })
      },
      /**
       * Resets/clears the form
       */
      clearForm () {
        this.form = Object.assign({}, this.formInitState)
        for (let field in this.form) {
          if (typeof this.form[field] === 'object') {
            switch (field) {
              case 'alias_type':
                this.form[field] = Object.assign({}, { label: 'Name', value: 'NAME' })
                break
              case 'default_address':
                this.form[field] = Object.assign({}, { label: 'Contact Address', value: 'CONTACT' })
                break
              case 'entity_type':
                this.form[field] = Object.assign({}, { label: 'Unknown', value: 'UNKNOWN' })
                break
              case 'default_payment_type':
                this.form[field] = Object.assign({}, { label: 'Check', value: 'CHECK' })
                break
              case 'default_markup_type':
                this.form[field] = Object.assign({}, { label: 'Not Set', value: 'NON' })
                break
              case 'billing_notification_method':
                this.form[field] = Object.assign({}, { label: 'E-mail', value: 'EML' })
                break
              case 'default_payment_method':
                this.form[field] = Object.assign({}, { label: 'Check', value: 'CHK' })
                break
              default:
                this.form[field] = Object.assign({}, { label: '', value: '' })
            }
          }
        }
        this.initialForm = JSON.stringify(this.form)
      },
      /**
       * Form change handler
       */
      formChanged (value) {
        if (value) {
          this.trackFormChange()
          this.inputChange = value.target.name === 'name' || value.target.name === 'tax_id'
          this.saved = false
        }
      },
      /**
       * Handles blur events
       * Currently autofills other fields whenever a user blurs out of any address field or
       * blurs out of the 'name' field
       * @param fieldName
       */
      handleBlur (fieldName) {
        const isAutocompleteField = (field) => (typeof field === 'object' && field.hasOwnProperty('value'))
        const fieldIsEmpty = (field) => isAutocompleteField(field) ? !field.value : !field
        // Populate state country if country is empty when state is selected
        if (['address_contact_state', 'address_billing_state', 'address_mailing_state'].includes(fieldName)) {
          const fieldNameArr = fieldName.split('_')
          const countryFieldName = fieldNameArr[0] + '_' + fieldNameArr[1] + '_country'
          if (this.form[countryFieldName].hasOwnProperty('value') && !this.form[countryFieldName]['value']) {
            const selectedState = (this.form[fieldName] && this.form[fieldName].hasOwnProperty('value')) ? this.form[fieldName]['value'] : this.form[fieldName]
            const stateObject = this.$store.getters.getAllStates.find((v) => v.id === selectedState)
            if (stateObject && stateObject.country) {
              let countryToBeSelected = this.getCountryById(stateObject.country)
              this.form[countryFieldName] = { label: countryToBeSelected.name, value: countryToBeSelected.id }
            }
          }
        }
        if (!this.editing && fieldName && fieldName.includes('address_contact')) {
          const f = fieldName.split('_')[2]
          if (fieldIsEmpty(this.form[`address_billing_${f}`])) {
            this.form[`address_billing_${f}`] = (f === 'state' || f === 'country') ? Object.assign({}, this.form[fieldName]) : this.form[fieldName]
          }
          if (fieldIsEmpty(this.form[`address_mailing_${f}`])) {
            this.form[`address_mailing_${f}`] = (f === 'state' || f === 'country') ? Object.assign({}, this.form[fieldName]) : this.form[fieldName]
          }
        } else if (!this.editing && fieldName && fieldName === 'name') {
          this.checkDuplicateVendor('name')
          if (!this.form.alias_name) this.form.alias_name = this.form.name
          if (!this.form.payee_name) this.form.payee_name = this.form.name
          if (!this.form.tax_name) this.form.tax_name = this.form.name
        } else if (!this.editing && this.inputChange && fieldName && fieldName === 'tax_id') {
          this.checkDuplicateVendor('tax_id')
        } else if (this.editing && this.inputChange && fieldName && fieldName === 'name') this.checkDuplicateVendor('name')
      },
      /**
       * Focuses on first logical field after modal is opened. If editing and a code exists, focusses on the
       * 'code' field, otherwise, focuses on 'name' field.
       */
      focusOnFirstField () {
        setTimeout(() => {    // Need to wait for modal to render, otherwise elements won't be there
          let firstFocus
          if (this.editing && this.form && this.form.code) {
            firstFocus = document.getElementsByName('code')
          } else if (!this.editing && this.form && this.form.hasOwnProperty('name')) {
            firstFocus = document.getElementsByName('name')
          }
          if (firstFocus && firstFocus[0]) firstFocus[0].focus()
        }, 400)
      },
      setFormId (id) {
        if (this.form.hasOwnProperty(`${id}_id`)) {
          this.form[id]['id'] = this.form[`${id}_id`]
        }
      },
      // Open View/Upload Documents modal
      async openUploadDocument (val) {
        this.dropzoneVisible = true
        // Re-render the document form to get the uploaded documents
        this.reRender++
      },
      // Show the file count
      dzSuccess () {
        this.numberOfFiles += 1
      },
      // Show the file count
      dzRemove () {
        this.numberOfFiles += -1
      },
      // Get Vendor file count
      getFileCount (data) {
        // API to get the file info
        this.$http.get(data.signingUrl, { headers: data.signingHeaders })
          .then(resp => {
            // Assing the file count
            this.numberOfFiles = resp.body && resp.body.count ? resp.body.count : 0
          })
          .catch(error => {
            console.log(error)
            this.numberOfFiles = 0
          })
      },
      // Check vendor has transactions
      checkVendorHasTransactions () {
        this.vendorHasTransactions = true
        this.$store.dispatch('vendorHasTransactions', { id: this.modelId })
          .then(r => {
            if (r) {
              this.vendorHasTransactions = r.is_vendor_present
              return r.is_vendor_present
            }
          })
      },
      // Function to setup a dropzone instance
      setupDropzoneOptions () {
        // Get active instance
        let instance = this.$store.getters.getActiveInstance
        // Get instance URL from instance data
        let instanceUrl = (instance.url.endsWith('/') ? instance.url.slice(0, -1) : instance.url)
        // Get route path and append vendor row id
        let path = this.editing && this.modelId ? `/vendors/${this.modelId}` : this.$route.path
        // Prepare payload in required format
        return {
          instanceUrl: instanceUrl,
          url: instanceUrl + path + '/files',
          headers: '',
          signingUrl: instanceUrl + path + '/files',
          signingHeaders: { 'Authorization': 'Bearer ' + instance.token },
          acceptedFiles: '.txt,.pdf,.jpg,.png,.tif,.tiff,.doc,.docx,.xls,.xlsx',
          autoProcessQueue: false,
          maxFileSize: 8,
          uploadMultiple: false,
          parallelUploads: 1
        }
      }
    },
    computed: {
      ...mapGetters({
        getAccountById: 'getAccountById',
        getAddressById: 'getAddressById',
        getVendorById: 'getVendorById',
        getCustomerById: 'getCustomerById',
        getCurrencyById: 'getCurrencyById',
        getSalesrepById: 'getSalesrepById',
        getStateById: 'getStateById',
        getCountryById: 'getCountryById',
        savingVendor: 'getVendorSaveStatus',
        savingCustomer: 'getCustomerSaveStatus',
        updatingVendor: 'getVendorUpdateStatus',
        updatingCustomer: 'getCustomerUpdateStatus',
        allStates: 'getAllStates',
        getTaxcodeById: 'getTaxcodeById',
        statesCount: 'getStateMetadata',
        salesreps: 'getAllSalesrep'
      }),
      postableAccounts () {
        return this.$store.getters.getAllAccounts.filter(account => account.postable)
      },
      liabilityAccounts () {
        return this.postableAccounts.filter(account => account.type === 'LIABILITY')
      },
      assetAccounts () {
        return this.postableAccounts.filter(account => account.type === 'ASSET')
      },
      pcAccounts () {
        return this.postableAccounts.filter(account => account.type === 'ASSET' || account.type === 'ASSET_INVENTORY')
      },
      pCardAccounts () {
        return this.postableAccounts.filter(account => account.type === 'ASSET' || account.type === 'ASSET_INVENTORY')
      },
      states () {
        return this.$store.getters.getAllStates.map(v => {
          return { label: v.name, value: v.id }
        })
      },
      countries () {
        return this.$store.getters.getCountries.map(v => {
          return { label: v.name, value: v.id }
        })
      },
      taxCodes () {
        return this.$store.getters.getAllTaxcodes.map(v => {
          return { label: v.name, value: v.id }
        })
      },
      pettyCashAccounts () {
        return this.$store.getters.getAllAccounts.filter(account => account.type === 'ASSET')
      },
      visible () {
        const visible = this.$store.getters.getModalVisibilityState
        if (visible.customerVendorForm && typeof visible.customerVendorForm === 'object' && visible.customerVendorForm.visible) {
          return visible.customerVendorForm.visible
        }
        return false
      },
      inDataEntryView () {
        const visible = this.$store.getters.getModalVisibilityState
        if (visible.customerVendorForm && typeof visible.customerVendorForm === 'object' && visible.customerVendorForm.inDataEntryView) {
          return visible.customerVendorForm.inDataEntryView
        }
        return false
      },
      capitalizedType () {
        return capitalizeFirstLetter(this.type)
      },
      customerVendorFields () {
        const defaultPCSetting = () => !!(this.inDataEntryView && ['NewPc', 'EditPc'].includes(this.$route.name))
        const model = this[`get${this.capitalizedType}ById`](this.modelId)
        let Payeedisable = model.lock_payeename
        const isVendor = this.type === 'vendor'
        let fields = [
          {
            label: 'Code',
            name: 'code',
            dataType: 'string',
            fieldType: 'input',
            placeholder: 'Auto-generated',
            idNum: 1
          },
          {
            label: 'Default account',
            name: 'default_accounts',
            dataType: 'string',
            fieldType: 'input',
            autocomplete: true,
            searchAsync: false,
            triggerOnFocus: false,
            idNum: 12,
            options: () => new AutocompleteLocalSearch(this.postableAccounts, accountActionPayload).createOptions(),
            actionPayload: { ...accountActionPayload, query: { postable: 'True' } }
          },
          {
            label: 'Name',
            name: 'name',
            dataType: 'string',
            fieldType: 'input',
            required: true,
            msg: 'Please specify a name',
            idNum: 2
          },
          {
            label: 'Customer Account #',
            name: 'account_number',
            dataType: 'string',
            fieldType: 'input',
            idNum: isVendor ? 11 : 17
          },
          {
            label: 'Payee name',
            name: 'payee_name',
            dataType: 'string',
            required: true,
            disabled: Payeedisable,
            fieldType: 'input',
            idNum: 3
          },
          {
            label: 'Petty cash enabled',
            name: 'petty_cash_enabled',
            dataType: 'boolean',
            fieldType: 'checkbox',
            disabled: defaultPCSetting(),
            idNum: isVendor ? 13 : 19
          },
          {
            label: 'Tax ID',
            name: 'tax_id',
            dataType: 'string',
            fieldType: 'input',
            idNum: 9
          },
          {
            label: 'Petty cash account',
            name: 'petty_cash_account',
            dataType: 'string',
            fieldType: 'input',
            autocomplete: true,
            searchAsync: false,
            triggerOnFocus: false,
            required: defaultPCSetting(),
            idNum: isVendor ? 14 : 20,
            options: () => new AutocompleteLocalSearch(this.pcAccounts, accountActionPayload).createOptions(),
            actionPayload: { ...accountActionPayload, query: { postable: 'True', type__in: ['ASSET', 'ASSET_INVENTORY'] } }
          },
          {
            label: 'Alias type',
            name: 'alias_type',
            dataType: 'string',
            fieldType: 'select',
            remote: false,
            idNum: 5,
            options: [
              { label: 'Name', value: 'NAME' },
              { label: 'Company name/DBA', value: 'DBA' },
              { label: 'FSO', value: 'FSO' }
            ]
          },
          {
            label: 'Pcard enabled',
            name: 'petty_cash_p_card_enabled',
            dataType: 'boolean',
            fieldType: 'checkbox',
            disabled: defaultPCSetting(),
            idNum: isVendor ? 15 : 21
          },
          {
            label: 'Alias name',
            name: 'alias_name',
            dataType: 'string',
            fieldType: 'input',
            required: true,
            idNum: 6
          },
          {
            label: 'Pcard account',
            name: 'petty_cash_p_card_account',
            dataType: 'string',
            fieldType: 'input',
            autocomplete: true,
            searchAsync: false,
            triggerOnFocus: false,
            required: defaultPCSetting(),
            idNum: isVendor ? 16 : 22,
            options: () => new AutocompleteLocalSearch(this.pCardAccounts, accountActionPayload).createOptions(),
            actionPayload: { ...accountActionPayload, query: { postable: 'True', type__in: ['ASSET', 'ASSET_INVENTORY'] } }
          },
          {
            label: 'Card Id',
            name: 'card_id',
            dataType: 'string',
            fieldType: 'input',
            idNum: isVendor ? 11 : 17
          },
          {
            label: 'Default payment type',
            name: 'default_payment_type',
            dataType: 'string',
            fieldType: 'select',
            required: true,
            remote: false,
            idNum: 18,
            options: [
              { label: 'Check', value: 'CHECK', selected: true },
              { label: 'Wire transfer', value: 'WIRE' },
              { label: 'EFT', value: 'EFT' },
              { label: 'Manual check', value: 'CHECK_MANUAL' }
            ]
          },
          {
            label: 'Work state',
            name: 'work_state',
            placeholder: 'Select a state...',
            dataType: 'string',
            fieldType: 'select',
            remote: false,
            filterable: true,
            idNum: 16,
            options: this.states
          },
          {
            label: 'Cell Phone',
            name: 'phone_cell',
            dataType: 'string',
            fieldType: 'input'
          },
          {
            label: 'Entity type',
            name: 'entity_type',
            dataType: 'string',
            fieldType: 'select',
            remote: false,
            required: true,
            idNum: 7,
            options: [
              { label: 'Not a 1099 Vendor', value: 'NOT_1099' },
              { label: 'Corporation', value: 'CORPORATION' },
              { label: 'LLC', value: 'LLC' },
              { label: 'LLC (Classified as a Corp)', value: 'LLC_CORP' },
              { label: 'Legal Firm', value: 'LEGAL_FIRM' },
              { label: 'Partnership', value: 'PARTNERSHIP' },
              { label: 'Sole Proprietor (SSN)', value: 'SOLE_PROPRIETOR_SSN' },
              { label: 'Sole Proprietor (EIN)', value: 'SOLE_PROPRIETOR_EIN' },
              { label: 'Individual', value: 'INDIVIDUAL' },
              { label: 'Other', value: 'OTHER' },
              { label: 'Not Selected/Unknown', value: 'UNKNOWN' }
            ]
          },
          {
            label: '',
            name: '',
            dataType: 'string',
            fieldType: ''
          },
          {
            label: 'FATCA Filing Requirement',
            name: 'fatca_filing_requirement',
            dataType: 'boolean',
            fieldType: 'checkbox',
            idNum: 32
          },
          {
            label: 'Home Phone',
            name: 'phone_home',
            dataType: 'string',
            fieldType: 'input'
          },
          {
            label: 'Email Address',
            name: 'email',
            dataType: 'string',
            fieldType: 'input'
          },
          {
            label: 'Parent',
            name: 'parent',
            dataType: 'string',
            fieldType: 'input',
            autocomplete: true,
            searchAsync: true,
            triggerOnFocus: false,
            idNum: 17
          },
          {
            label: 'Active',
            name: 'active',
            disabled: this.inDataEntryView,
            dataType: 'boolean',
            fieldType: 'checkbox',
            idNum: 8
          },
          {
            label: 'Legal/Tax Name',
            name: 'tax_name',
            dataType: 'string',
            fieldType: 'input',
            idNum: isVendor ? 18 : 24
          },
          {
            label: 'Default address',
            name: 'default_address',
            dataType: 'string',
            fieldType: 'select',
            remote: false,
            required: true,
            idNum: 9,
            options: [
              { label: 'Billing Address', value: 'BILLING' },
              { label: 'Mailing Address', value: 'MAILING' },
              { label: 'Contact Address', value: 'CONTACT', selected: true }
            ]
          },
          {
            label: 'Due days',
            name: 'due_days',
            dataType: 'string',
            fieldType: 'input',
            idNum: isVendor ? 16 : 22
          },
          {
            label: 'ACH Bank Routing #',
            name: 'ach_routing_number',
            dataType: 'string',
            fieldType: 'input',
            idNum: isVendor ? 11 : 17
          },
          {
            label: 'Description',
            name: 'description',
            dataType: 'string',
            fieldType: 'input',
            idNum: 4
          },
          {
            label: 'ACH Bank Account #',
            name: 'ach_account_number',
            dataType: 'string',
            fieldType: 'input',
            idNum: isVendor ? 11 : 17
          },
          {
            label: 'Tax Code',
            name: 'default_tax_code',
            dataType: 'string',
            fieldType: 'select',
            remote: false,
            idNum: 10,
            options: this.taxCodes
          }
        ]

        // const ignoreTaxCodes = [
        //   'NOT_1099',
        //   'CORPORATION',
        //   'LLC_CORP',
        //   'UNKNOWN'
        // ]
        // if (!ignoreTaxCodes.includes(this.form.entity_type.value)) {
        //   fields.push({
        //     label: 'Tax Code',
        //     name: 'default_tax_code',
        //     dataType: 'string',
        //     fieldType: 'select',
        //     remote: false,
        //     idNum: 10,
        //     options: this.taxCodes
        //   })
        //   this.warning = this.form.default_tax_code.value ? '' : 'Please specify a tax code'
        // } else {
        //   this.form.default_tax_code = Object.assign({}, { label: '', value: '' })
        //   this.warning = ''
        // }
        if (this.type === 'customer') {
          const customerFields = [
            {
              label: 'Credit Limit',
              name: 'credit_limit_decimal',
              required: false,
              dataType: 'string',
              fieldType: 'input',
              idNum: 26
            },
            {
              label: 'Currency',
              name: 'currency',
              required: true,
              dataType: 'string',
              fieldType: 'input',
              autocomplete: true,
              searchAsync: true,
              triggerOnFocus: false,
              idNum: 11
            },
            {
              label: 'Payment term in days',
              name: 'payment_term',
              required: false,
              dataType: 'string',
              fieldType: 'input',
              idNum: 27
            },
            {
              label: 'Receivable Account',
              name: 'receivable_account',
              required: true,
              dataType: 'string',
              fieldType: 'input',
              autocomplete: true,
              searchAsync: false,
              triggerOnFocus: false,
              idNum: 12,
              options: () => new AutocompleteLocalSearch(this.assetAccounts, accountActionPayload).createOptions(),
              actionPayload: { ...accountActionPayload, query: { type: 'ASSET' } }
            },
            {
              label: 'Sales Rep',
              name: 'sales_rep',
              required: false,
              dataType: 'string',
              fieldType: 'input',
              autocomplete: true,
              searchAsync: false,
              triggerOnFocus: false,
              idNum: 28,
              options: () => new AutocompleteLocalSearch(this.salesreps, salesrepActionPayload).createOptions(),
              actionPayload: salesrepActionPayload
            },
            {
              label: 'Petty Cash Custodian',
              name: 'petty_cash_custodian',
              fieldType: 'checkbox',
              dataType: 'boolean',
              required: false,
              idNum: 13
            },
            {
              label: 'PC Custodian Account',
              name: 'petty_cash_custodian_account',
              required: true,
              dataType: 'string',
              fieldType: 'input',
              autocomplete: true,
              searchAsync: false,
              triggerOnFocus: false,
              idNum: 29,
              options: () => new AutocompleteLocalSearch(this.postableAccounts, accountActionPayload).createOptions(),
              actionPayload: { ...accountActionPayload, query: { postable: 'True' } }
            },
            {
              label: 'Default Payment Method',
              name: 'default_payment_method',
              dataType: 'string',
              fieldType: 'select',
              required: false,
              options: [
                { label: 'Check', value: 'CHK' },
                { label: 'Automated Clearing House', value: 'ACH' }
              ],
              idNum: 14
            },
            {
              label: 'Billing Notification Method',
              name: 'billing_notification_method',
              dataType: 'string',
              fieldType: 'select',
              required: false,
              options: [
                { label: 'E-mail', value: 'EML', selected: true },
                { label: 'Print', value: 'PRT' }
              ],
              idNum: 30
            },
            {
              label: 'Deposit Account',
              name: 'deposit_account',
              required: true,
              dataType: 'string',
              fieldType: 'input',
              autocomplete: true,
              searchAsync: false,
              triggerOnFocus: false,
              idNum: 15,
              options: () => new AutocompleteLocalSearch(this.liabilityAccounts, accountActionPayload).createOptions(),
              actionPayload: { ...accountActionPayload, query: { type: 'LIABILITY' } }
            },
            {
              label: 'Comments',
              name: 'comments',
              dataType: 'string',
              fieldType: 'input',
              required: false,
              idNum: 31
            }
          ]
          fields = fields.filter((f) => !['', 'fatca_filing_requirement'].includes(f.name))
          fields = [...fields, ...customerFields]
        }
        return fields
      },
      contactAddressFields () {
        return [
          {
            label: 'Contact address line 1',
            name: 'address_contact_line1',
            dataType: 'string',
            fieldType: 'input',
            required: false,
            idNum: 1
          },
          {
            label: 'Contact state',
            name: 'address_contact_state',
            placeholder: 'Select a state...',
            dataType: 'string',
            fieldType: 'select',
            remote: false,
            filterable: true,
            required: false,
            idNum: 4,
            options: this.states
          },
          {
            label: 'Contact address line 2',
            name: 'address_contact_line2',
            dataType: 'string',
            fieldType: 'input',
            idNum: 2
          },
          {
            label: 'Contact postal',
            name: 'address_contact_postal',
            dataType: 'string',
            fieldType: 'input',
            required: false,
            idNum: 5
          },
          {
            label: 'Contact city',
            name: 'address_contact_city',
            dataType: 'string',
            fieldType: 'input',
            required: false,
            idNum: 3
          },
          {
            label: 'Contact Country',
            name: 'address_contact_country',
            placeholder: 'Select a country...',
            dataType: 'string',
            fieldType: 'select',
            remote: false,
            filterable: true,
            required: false,
            multiple: false,
            idNum: 6,
            options: this.countries
          }
        ]
      },
      mailingAddressFields () {
        return [
          {
            label: 'Mailing address line 1',
            name: 'address_mailing_line1',
            dataType: 'string',
            fieldType: 'input',
            required: false,
            idNum: 1
          },
          {
            label: 'Mailing state',
            name: 'address_mailing_state',
            placeholder: 'Select a state...',
            dataType: 'string',
            fieldType: 'select',
            remote: false,
            filterable: true,
            required: false,
            idNum: 4,
            options: this.states
          },
          {
            label: 'Mailing address line 2',
            name: 'address_mailing_line2',
            dataType: 'string',
            fieldType: 'input',
            idNum: 2
          },
          {
            label: 'Mailing postal',
            name: 'address_mailing_postal',
            dataType: 'string',
            fieldType: 'input',
            required: false,
            idNum: 5
          },
          {
            label: 'Mailing city',
            name: 'address_mailing_city',
            dataType: 'string',
            fieldType: 'input',
            required: false,
            idNum: 3
          },
          {
            label: 'Mailing Country',
            name: 'address_mailing_country',
            placeholder: 'Select a country...',
            dataType: 'string',
            fieldType: 'select',
            remote: false,
            filterable: true,
            required: false,
            multiple: false,
            idNum: 6,
            options: this.countries
          }
        ]
      },
      billingAddressFields () {
        return [
          {
            label: 'Billing address line 1',
            name: 'address_billing_line1',
            dataType: 'string',
            fieldType: 'input',
            required: false,
            idNum: 1
          },
          {
            label: 'Billing state',
            name: 'address_billing_state',
            placeholder: 'Select a state...',
            dataType: 'string',
            fieldType: 'select',
            remote: false,
            filterable: true,
            required: false,
            idNum: 4,
            options: this.states
          },
          {
            label: 'Billing address line 2',
            name: 'address_billing_line2',
            dataType: 'string',
            fieldType: 'input',
            idNum: 2
          },
          {
            label: 'Billing postal',
            name: 'address_billing_postal',
            dataType: 'string',
            fieldType: 'input',
            required: false,
            idNum: 5
          },
          {
            label: 'Billing city',
            name: 'address_billing_city',
            dataType: 'string',
            fieldType: 'input',
            required: false,
            idNum: 3
          },
          {
            label: 'Billing Country',
            name: 'address_billing_country',
            placeholder: 'Select a country...',
            dataType: 'string',
            fieldType: 'select',
            remote: false,
            filterable: true,
            required: false,
            multiple: false,
            idNum: 6,
            options: this.countries
          }
        ]
      }
    },
    watch: {
      /**
       * If we're currently in a data entry view (like EditAp or NewPc),
       * we need to update the fields accordingly.
       */
      inDataEntryView () {
        if (this.inDataEntryView) {
          this.formInitState.petty_cash_enabled = ['NewPc', 'EditPc'].includes(this.$route.name)
        }
      },
      /**
       * If the modal becomes visible, clears the form and sets the vendor code
       */
      visible () {
        if (this.visible) {
          this.clearForm()
        }
      },
      /**
       * Checks whether edit mode is active
       */
      modelId () {
        if (this.modelId && typeof this.modelId === 'string') {
          this.loading = true
          this.editing = true
          this.editExistingModel(this.modelId)
        }
      }
    },
    created () {
      // Create a blank copy of the form on component creation
      this.formInitState = Object.assign({}, this.form)
    },
    async mounted () {
      if (this.isCityModel === undefined) {
        await this.checkCityModel()
      }
    }
  }
</script>

<style scoped>
.delete-vendor:disabled {
  pointer-events: auto;
}
.delete-vendor:disabled:hover:after {
  content: "Vendor is in use";
  position: absolute;
  bottom: 55px;
  padding: 10px;
  right: 200px;
  z-index: 1000;
  border: 1px solid black;
  border-radius: 5px;
  background-color: black;
  color: white;
}
</style>



// WEBPACK FOOTER //
// src/components/FormComponents/CustomerVendorForm.vue